/**
 * DepositsModule - Vuex store module for deposits and damage waivers tracking
 *
 * 2026-02-04: Changed default date range from 2 weeks to 1 day
 * 2026-02-03: Added pending stats getters for KPIs
 * 2026-02-03: Added pending back to default statuses
 * 2026-02-03: Added default date range filter (last 2 weeks)
 * 2026-02-03: Changed status to multi-select array
 * 2026-02-03: Created deposits tracking module for unified deposit/waiver view
 *
 * This module handles state management for:
 * - Security Deposits (from PaymentIntent)
 * - Damage Waivers (from StoreItemPurchase)
 * - Filtering, pagination, and statistics
 */
import axios from '@/axios/config'

// Helper to get default date range (last 1 day)
const getDefaultDateRange = () => {
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  return {
    fromDate: yesterday.toISOString().split('T')[0],
    toDate: today.toISOString().split('T')[0],
  }
}

export default {
  namespaced: true,
  state: {
    deposits: [],
    statistics: {},
    loading: false,
    error: null,
    filters: {
      listingId: null,
      ...getDefaultDateRange(),
      statuses: [
        'pending',
        'authorized',
        'captured',
        'released',
        'cancelled',
        'refunded',
      ],
      type: null,
      refundStatus: null,
    },
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      perPage: 25,
    },
  },
  mutations: {
    setLoading(state, loading) {
      state.loading = loading
    },
    setError(state, error) {
      state.error = error
    },
    setDeposits(state, deposits) {
      state.deposits = deposits
    },
    setStatistics(state, statistics) {
      state.statistics = statistics
    },
    setFilters(state, filters) {
      state.filters = { ...state.filters, ...filters }
    },
    setPagination(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination }
    },
    clearError(state) {
      state.error = null
    },
    resetFilters(state) {
      state.filters = {
        listingId: null,
        ...getDefaultDateRange(),
        statuses: [
          'pending',
          'authorized',
          'captured',
          'released',
          'cancelled',
          'refunded',
        ],
        type: null,
        refundStatus: null,
      }
    },
  },
  actions: {
    clearError({ commit }) {
      commit('clearError')
    },

    /**
     * Fetch deposits with optional filters and pagination
     *
     * @param {Object} params - Pagination parameters
     * @param {Number} params.page - Page number
     * @param {Number} params.perPage - Items per page
     */
    async fetchDeposits({ commit, state }, { page = 1, perPage = 25 } = {}) {
      commit('setLoading', true)
      commit('clearError')

      try {
        const params = {
          page,
          per_page: perPage,
        }

        // Add filters
        if (state.filters.listingId) {
          params.listing_id = state.filters.listingId
        }
        if (state.filters.fromDate) {
          params.from_date = state.filters.fromDate
        }
        if (state.filters.toDate) {
          params.to_date = state.filters.toDate
        }
        if (state.filters.statuses && state.filters.statuses.length > 0) {
          params.statuses = state.filters.statuses.join(',')
        }
        if (state.filters.type) {
          params.type = state.filters.type
        }
        if (state.filters.refundStatus) {
          params.refund_status = state.filters.refundStatus
        }

        const { data } = await axios.get('/api/deposits', { params })

        const deposits = data?.deposits || []
        const paginationData = data?.pagination || {}

        const pagination = {
          currentPage: paginationData.current_page || 1,
          totalCount: paginationData.total_count || 0,
          perPage: paginationData.per_page || 25,
          totalPages: paginationData.total_pages || 1,
        }

        commit('setDeposits', deposits)
        commit('setPagination', pagination)

        return { success: true, data: deposits }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Failed to fetch deposits'
        commit('setError', errorMessage)
        console.error('Deposits: Error fetching deposits:', error)

        return { success: false, error: errorMessage }
      } finally {
        commit('setLoading', false)
      }
    },

    /**
     * Fetch deposit statistics
     */
    async fetchStatistics({ commit, state }) {
      try {
        const params = {}

        // Add filters
        if (state.filters.listingId) {
          params.listing_id = state.filters.listingId
        }
        if (state.filters.fromDate) {
          params.from_date = state.filters.fromDate
        }
        if (state.filters.toDate) {
          params.to_date = state.filters.toDate
        }

        const { data } = await axios.get('/api/deposits/statistics', { params })

        const stats = data?.statistics || {}
        commit('setStatistics', stats)

        return { success: true, data: stats }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || 'Failed to fetch deposit statistics'
        console.error('Deposits: Error fetching statistics:', error)

        return { success: false, error: errorMessage }
      }
    },

    /**
     * Update filters and refetch deposits
     *
     * @param {Object} filters - New filter values
     */
    async updateFilters({ commit, dispatch }, filters) {
      commit('setFilters', filters)
      await Promise.all([
        dispatch('fetchDeposits', { page: 1 }),
        dispatch('fetchStatistics'),
      ])
    },

    /**
     * Reset all filters and refetch deposits
     */
    async resetFilters({ commit, dispatch }) {
      commit('resetFilters')
      await Promise.all([
        dispatch('fetchDeposits', { page: 1 }),
        dispatch('fetchStatistics'),
      ])
    },

    /**
     * Export deposits to CSV
     *
     * @returns {Promise<Blob>} CSV file blob
     */
    async exportDeposits({ state }) {
      try {
        const params = {}

        // Add filters
        if (state.filters.listingId) {
          params.listing_id = state.filters.listingId
        }
        if (state.filters.fromDate) {
          params.from_date = state.filters.fromDate
        }
        if (state.filters.toDate) {
          params.to_date = state.filters.toDate
        }
        if (state.filters.statuses && state.filters.statuses.length > 0) {
          params.statuses = state.filters.statuses.join(',')
        }
        if (state.filters.type) {
          params.type = state.filters.type
        }
        if (state.filters.refundStatus) {
          params.refund_status = state.filters.refundStatus
        }

        const response = await axios.get('/api/deposits/export', {
          params,
          responseType: 'blob',
        })

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `deposits_${new Date().toISOString().split('T')[0]}.csv`
        )
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        return { success: true }
      } catch (error) {
        console.error('Deposits: Error exporting deposits:', error)
        return { success: false, error: 'Failed to export deposits' }
      }
    },
  },
  getters: {
    isLoading: state => state.loading,
    hasError: state => !!state.error,
    errorMessage: state => state.error,
    allDeposits: state => state.deposits,
    statistics: state => state.statistics,
    filters: state => state.filters,
    pagination: state => state.pagination,
    hasFiltersApplied: state => {
      const { statuses, fromDate, toDate, ...otherFilters } = state.filters
      const defaultStatuses = [
        'pending',
        'authorized',
        'captured',
        'released',
        'cancelled',
        'refunded',
      ]
      const defaultDates = getDefaultDateRange()
      const statusesChanged =
        JSON.stringify(statuses?.sort()) !==
        JSON.stringify(defaultStatuses.sort())
      const datesChanged =
        fromDate !== defaultDates.fromDate || toDate !== defaultDates.toDate
      return (
        statusesChanged ||
        datesChanged ||
        Object.values(otherFilters).some(v => v !== null)
      )
    },

    // Filtered counts from statistics
    totalCount: state => state.statistics.total_count || 0,
    heldCount: state => state.statistics.held_count || 0,
    releasedCount: state => state.statistics.released_count || 0,
    pendingCount: state => state.statistics.pending_count || 0,
    securityDepositsCount: state =>
      state.statistics.security_deposits_count || 0,
    damageWaiversCount: state => state.statistics.damage_waivers_count || 0,
    damageProtectionCount: state =>
      state.statistics.damage_protection_count || 0,
    heldAmount: state => state.statistics.held_amount || 0,
    releasedAmount: state => state.statistics.released_amount || 0,
    pendingAmount: state => state.statistics.pending_amount || 0,
    authorizedCount: state => state.statistics.authorized_count || 0,
    authorizedAmount: state => state.statistics.authorized_amount || 0,
  },
}
